import { useEffect, useRef, useState, useCallback } from 'react';

interface OtpErrorData {
  status?: number;
  data?: {
    message?: string;
    error?: {
      retryAfterSeconds?: number;
    };
  };
}

export function useOtpCooldown(storageKey: string) {
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(() => {
    const storedCooldownTimestamp = localStorage.getItem(storageKey);
    if (storedCooldownTimestamp) {
      const cooldownUntil = parseInt(storedCooldownTimestamp, 10);
      const now = Date.now();
      const remainingSeconds = Math.ceil((cooldownUntil - now) / 1000);

      if (remainingSeconds > 0) {
        return remainingSeconds;
      }
      localStorage.removeItem(storageKey);
    }
    return 0;
  });

  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Countdown timer based on localStorage timestamp
  useEffect(() => {
    if (resendCooldownSeconds > 0) {
      cooldownTimerRef.current = setInterval(() => {
        const storedTimestamp = localStorage.getItem(storageKey);
        if (storedTimestamp) {
          const cooldownUntil = parseInt(storedTimestamp, 10);
          const remainingSeconds = Math.ceil(
            (cooldownUntil - Date.now()) / 1000
          );

          if (remainingSeconds <= 0) {
            setResendCooldownSeconds(0);
            localStorage.removeItem(storageKey);
            if (cooldownTimerRef.current)
              clearInterval(cooldownTimerRef.current);
          } else {
            setResendCooldownSeconds(remainingSeconds);
          }
        }
      }, 1000);

      return () => {
        if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      };
    }
  }, [resendCooldownSeconds, storageKey]);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  // Extract retry seconds from error message
  const extractRetrySeconds = (errorMessage: string): number => {
    const match = errorMessage.match(/try again in (\d+) seconds/i);
    return match ? parseInt(match[1], 10) : 300;
  };

  // Handle 429 error and set cooldown
  const handleResendError = (
    error: unknown
  ): { message: string; retrySeconds: number } => {
    let message = 'Failed to resend';
    let retrySeconds = 0;

    if (typeof error === 'object' && error !== null) {
      const err = error as OtpErrorData;

      // Try to extract from nested error.retryAfterSeconds first
      if (err.data?.error?.retryAfterSeconds) {
        retrySeconds = err.data.error.retryAfterSeconds;
        message =
          err.data?.message || 'Too many requests. Please try again later.';
      } else if (err.data?.message) {
        message = err.data.message;
        retrySeconds = extractRetrySeconds(err.data.message);
      } else {
        message = 'Too many requests. Please try again later.';
        retrySeconds = 300;
      }

      // Set cooldown if we got a retry time
      if (retrySeconds > 0) {
        setResendCooldownSeconds(retrySeconds);
        const cooldownUntil = Date.now() + retrySeconds * 1000;
        localStorage.setItem(storageKey, cooldownUntil.toString());
      }
    }

    return { message, retrySeconds };
  };

  // Handle modal close with cleanup
  const handleClose = useCallback(() => {
    localStorage.removeItem(storageKey);
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
  }, [storageKey]);

  return {
    resendCooldownSeconds,
    setResendCooldownSeconds,
    cooldownTimerRef,
    extractRetrySeconds,
    handleResendError,
    handleClose,
  };
}
